import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { TenantDataService } from 'app/config/services/tenant-data.service';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { MaxBrainExperience } from 'app/library/experience/models/experience.model';
import { Feature } from 'app/library/feature/models/feature.model';
import { FeatureState } from 'app/library/feature/models/feature.state';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { FetchFinalGrades } from 'app/library/final-grade/actions';
import { ExperienceConfig } from 'app/library/grow/models/experience-config.model';
import { ExperienceWidgetContent } from 'app/library/grow/models/experience-widget-content.model';
import { HeaderBannerContentPreview } from 'app/library/grow/models/header-banner-content-preview.model';
import { Module } from 'app/library/module/models/module.model';
import { MyModulesState } from 'app/library/my-profile-modules/models/my-modules.state';
import { Posting } from 'app/library/posting/models/posting.model';
import { TagMultiSelectService } from 'app/library/tag/services/multi-select.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { GeneralAnalyticsService } from 'app/services/general-analytics.service';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SwiperOptions } from 'swiper/types/swiper-options';
import { EngageHomePageApiService } from '../services/grow-home-page.service';

@Component({
    selector: 'app-grow-home',
    templateUrl: './grow-home.component.html',
    styleUrls: ['./grow-home.component.scss'],
})
export class GrowHomeComponent extends UnsubscribeOnDestroy {
    @ViewChild('swiperElHeader', { static: false }) swiperElHeader!: ElementRef;
    @ViewChild('swiperElCoursel', { static: false }) swiperElCoursel!: ElementRef;

    @ViewChild('navBar')
    navBar: ElementRef;
    navBarPosition: number;
    arrayOfViewedElements: string[] = [];
    isExpanded = false;
    // we use this component for preview and for dashboard page for normal user

    @Select(FeatureState.getEntities)
    feature$: Observable<Feature[]>;
    isAlumniWidgetSectionVisible = false;

    @Select(UserState.getMyInfo)
    profile$: Observable<User>;
    profile: User;

    @Select(MyModulesState.getModules)
    myModule$: Module[];

    @Input()
    experienceConfig: ExperienceConfig;

    @Input()
    experience: MaxBrainExperience;

    @Input()
    audiences: MaxBrainAudience[];

    @Input()
    headerBannerContentPreview: HeaderBannerContentPreview;

    @Input()
    widgetGroups: Map<string, any>;

    @Input()
    featuredWidget: ExperienceWidgetContent;

    @Input()
    isPreview = false;

    @Input()
    isLoading = true;

    @Input()
    visibleWidgetCount: number = null;

    isCertificateOfAttendanceFeatureEnabled: boolean;
    isCommunityFeatureEnabled: boolean;
    isGrowSearchFeatureEnabled: boolean;

    public headerConfig: SwiperOptions = {
        a11y: { enabled: true },
        direction: 'horizontal',
        slidesPerView: 1,
        keyboard: false,
        mousewheel: false,
        scrollbar: false,
        pagination: false,
        navigation: false,
        updateOnWindowResize: true,
    };

    public headerConfigCourselView: SwiperOptions = {
        a11y: { enabled: true },
        direction: 'horizontal',
        slidesPerView: 1,
        keyboard: false,
        mousewheel: false,
        scrollbar: false,
        pagination: {
            el: '.swiper-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
        },
        updateOnWindowResize: true,
    };

    constructor(
        @Inject(DOCUMENT) private document: any,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _generalAnalyticsService: GeneralAnalyticsService,
        public tagMultiSelectService: TagMultiSelectService,
        private _tenantDataService: TenantDataService,
        private _featureService: FeatureService,
        private _engageApiService: EngageHomePageApiService,
        public router: Router,
        private _store: Store
    ) {
        super();

        if (this._featureService.checkFeatureStatus('gradebook') && this._tenantDataService.tenantConfig.gradebook.certificates) {
            this._store.dispatch(new FetchFinalGrades());
        }

        this.isCertificateOfAttendanceFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CertificateOfAttendance);
        this.isCommunityFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Community);
        this.isGrowSearchFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.GrowSearch);

        this.document.body.classList.add('engage');
    }

    ngOnInit(): void {
        this.profile$
            .pipe(
                filter((profile) => !!profile),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((profile) => {
                this.profile = profile;
            });

        this.feature$.pipe(takeUntil(this._unsubscribeAll)).subscribe((features) => {
            this.isAlumniWidgetSectionVisible = !!features.filter((feature) => feature.id === 'community' && feature.status === 'active').length;
        });
    }

    search(searchTerm: string, isExpanded: boolean): void {
        // search value only if search is expanded, otherwise expand it
        if (searchTerm && isExpanded) {
            this._engageApiService.growHomePageSearchTerm = searchTerm;
            this.router.navigate(['/grow/search']);
            this._generalAnalyticsService.createPostingSearchEvent('posting_search', searchTerm);
        } else if (!isExpanded) {
            this.isExpanded = true;
        }
    }

    ngAfterViewInit(): void {
        if (this.headerBannerContentPreview && !this.headerBannerContentPreview.content.length && this.swiperElHeader?.nativeElement) {
            Object.assign(this.swiperElHeader.nativeElement, this.headerConfig);
            this.swiperElHeader.nativeElement.initialize();
        }

        if (this.headerBannerContentPreview && this.headerBannerContentPreview.content.length && this.swiperElCoursel?.nativeElement) {
            Object.assign(this.swiperElCoursel.nativeElement, this.headerConfigCourselView);
            this.swiperElCoursel.nativeElement.initialize();
        }

        window.addEventListener('scroll', this.scroll, true);
        this.navBarPosition = this.navBar.nativeElement.offsetTop;

        if (this.headerBannerContentPreview.carouselView) {
            this.sendPostingEvent(this.headerBannerContentPreview.content[0].id);
        }
    }

    goToUpdateMyProfile() {
        this.router.navigate(['/profile/tab/profile-details']);
    }

    goToCommunityPage(widgetId: string) {
        this.router.navigate([`/community/${widgetId}`]);
    }

    goToAllModulesPage() {
        this.router.navigate(['/modules']);
    }

    onSlideChangeTransitionEnd() {
        if (this.headerBannerContentPreview.carouselView && this.router.url === '/grow') {
            /*  const slideIndex = this.componentRef.directiveRef.getIndex();
            const postingResourceId = this.headerBannerContentPreview.content[slideIndex].id;
            this.sendPostingEvent(postingResourceId); */
        }
    }

    openPostingUrl(postingResourceId: string, posting: Posting) {
        // If the url starts with the same domain as the custom domain (whole one, not just base domain) then open internally
        if (posting.url.includes(window.location.host)) {
            window.open(`${posting.url}`);
        } else {
            if (this.headerBannerContentPreview.carouselView) {
                const postingResource = this.headerBannerContentPreview.content.find((postingResource) => postingResource.id === postingResourceId);
                const currentPostUrl = postingResource.posting.url;

                if (this.router.url === '/grow') {
                    this._generalAnalyticsService.createPostingEvent(
                        'posting_link_followed',
                        postingResource.posting.id,
                        postingResource.posting.postingType.id,
                        postingResource.posting.translation.title,
                        postingResource.audience,
                        this.featuredWidget.id,
                        this.featuredWidget.name
                    );
                }

                window.open(currentPostUrl, '_blank');
            }
        }
    }

    scrollIntoView(widgetName: string): void {
        let element = document.getElementById(widgetName);
        element.scrollIntoView(true);
        element.focus();
    }

    scroll = (event: any): void => {
        const scrollTop = event.srcElement.scrollTop;
        if (scrollTop >= this.navBarPosition) {
            this.navBar.nativeElement.classList.add('sticky');
        } else {
            this.navBar.nativeElement.classList.remove('sticky');
        }
    };

    async sendPostingEvent(postingResourceId: string): Promise<void> {
        if (this.headerBannerContentPreview.carouselView && this.router.url === '/grow') {
            const postingResource = this.headerBannerContentPreview.content.find((postingResource) => postingResource.id === postingResourceId);
            this._generalAnalyticsService.createPostingEvent(
                'posting_viewed',
                postingResource.posting.id,
                postingResource.posting.postingType.id,
                postingResource.posting.translation.title,
                postingResource.audience,
                this.headerBannerContentPreview.widgetId,
                this.headerBannerContentPreview.widgetName
            );
        }
    }

    ngOnDestroy(): void {
        this.document.body.classList.remove('engage');
        super.ngOnDestroy();
    }
}
